import dayjs from 'dayjs';

export const TIMEFRAME_1D = '1D';
export const TIMEFRAME_1W = '1W';
export const TIMEFRAME_1M = '1M';
export const TIMEFRAME_CUSTOM = 'custom';

export const DEFAULT_PERF_FILTERS = {
  workflowGroups: ['ALL'],
  workflows: ['ALL'],
  apis: ['ALL'],
  adapters: ['ALL'],
  quantity: 10,
};

export const SOURCE_COLORS = ['#1e3a5f', '#5b4b8a', '#4a90c2', '#6aaf6a', '#d4a017', '#c45c5c', '#8e6bb8'];

/** Stacked violation series colors (matches performance mockup). */
export const VIOLATION_STACK_COLORS = {
  execTime: '#d4a017',
  validation: '#1e3a5f',
  translation: '#c45c9a',
  errors: '#5b4b8a',
};

/** Singular view: four violation stacks (execution time + workflow error types). */
export function buildViolationStackSeries(labels = {}) {
  return [
    {
      key: 'execTime',
      label: labels.execTime || 'Execution Time',
      tooltipLabel: labels.execTimeTooltip || labels.execTime || 'Exec. Time Errors',
      color: VIOLATION_STACK_COLORS.execTime,
    },
    {
      key: 'validation',
      label: labels.validation || 'Validation',
      tooltipLabel: labels.validation || 'Validation',
      color: VIOLATION_STACK_COLORS.validation,
    },
    {
      key: 'translation',
      label: labels.translation || 'Translation',
      tooltipLabel: labels.translation || 'Translation',
      color: VIOLATION_STACK_COLORS.translation,
    },
    {
      key: 'errors',
      label: labels.errors || 'Errors',
      tooltipLabel: labels.errors || 'Errors',
      color: VIOLATION_STACK_COLORS.errors,
    },
  ];
}

/** Dashboard summarized view: execution time + combined workflow errors. */
export function buildSummarizedViolationStackSeries(labels = {}) {
  return [
    {
      key: 'execTime',
      label: labels.execTime || 'Execution Time Errors',
      tooltipLabel: labels.execTimeTooltip || labels.execTime || 'Execution Time Errors',
      color: '#1e3a5f',
    },
    {
      key: 'workflow',
      label: labels.workflow || 'Workflow Errors',
      tooltipLabel: labels.workflow || 'Workflow Errors',
      color: '#e8943a',
    },
  ];
}

export function violationSegmentsFromRow(row) {
  return {
    execTime:
      (Number(row.exec_time_viol_cnt) || 0) + (Number(row.success_non_conform_cnt) || 0),
    validation: Number(row.err_validation_cnt) || 0,
    translation: Number(row.err_translation_cnt) || 0,
    errors: Number(row.err_generic_cnt) || 0,
  };
}

export function violationSegmentTotal(segments) {
  return (
    segments.execTime + segments.validation + segments.translation + segments.errors
  );
}

export function apiRowKey(row) {
  return `${row.workflowGroup || row.wf_group}::${row.workflowName || row.wf_name}`;
}

export function parseApiSelection(value) {
  const [workflowGroup, workflowName] = String(value).split('::');
  return { workflowGroup, workflowName };
}

export function startOfToday() {
  return dayjs().startOf('day');
}

export function endOfToday() {
  return dayjs().endOf('day');
}

export function defaultCustomRange() {
  return { from: startOfToday(), to: endOfToday() };
}

export function rangeForPreset(preset) {
  const now = dayjs();
  if (preset === TIMEFRAME_1W) {
    return { from: now.subtract(7, 'day').startOf('day'), to: now.endOf('day') };
  }
  if (preset === TIMEFRAME_1M) {
    return { from: now.subtract(1, 'month').startOf('day'), to: now.endOf('day') };
  }
  return defaultCustomRange();
}

/** Date range for Transaction Search click-through from performance charts. */
export function txnSearchRangeForTimeframe(timeframe, effectiveRange) {
  if (timeframe === TIMEFRAME_1D) {
    return { from: dayjs().subtract(24, 'hour'), to: dayjs() };
  }
  return effectiveRange;
}

export function formatDateParam(value) {
  if (!value) return '';
  return dayjs(value).format('YYYY-MM-DD');
}

export function formatDateTimeDisplay(value) {
  if (!value) return '';
  return dayjs(value).format('YYYY-MM-DD HH:mm');
}

export function isAllSelected(values) {
  return !values?.length || values.includes('ALL');
}

export function buildServiceNameMap(rows) {
  const map = new Map();
  (rows || []).forEach((row) => {
    map.set(apiRowKey(row), row.name || row.workflowName || row.wf_name);
  });
  return map;
}

export function buildServiceAccountMap(rows) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const account = row.account || row.wf_account;
    if (account) map.set(apiRowKey(row), account);
  });
  return map;
}

/** Resolve account for singular metrics: Redux account first, row account when portal is "all". */
export function resolveSingularAccount(reduxAccount, targetAccount) {
  const fromRedux = String(reduxAccount ?? '').trim();
  if (fromRedux && fromRedux.toLowerCase() !== 'all') return fromRedux;

  const fromTarget = String(targetAccount ?? '').trim();
  if (fromTarget && fromTarget.toLowerCase() !== 'all') return fromTarget;

  return undefined;
}

export function lookupServiceForBar(bar, serviceRows) {
  if (!bar || !serviceRows?.length) return undefined;

  const wg = String(bar.workflowGroup ?? '').trim();
  const wn = String(bar.workflowName ?? '').trim();

  if (wg && wn) {
    const exact = serviceRows.find(
      (r) => String(r.workflowGroup ?? '') === wg && String(r.workflowName ?? '') === wn
    );
    if (exact) return exact;
  }

  if (wn) {
    const byWorkflowName = serviceRows.find((r) => String(r.workflowName ?? '') === wn);
    if (byWorkflowName) return byWorkflowName;
  }

  const label = String(bar.label ?? '').trim();
  if (label) {
    return serviceRows.find(
      (r) => String(r.name ?? '') === label || String(r.workflowName ?? '') === label
    );
  }

  return undefined;
}

export function buildDetailTargetFromBar(bar, serviceRows) {
  const row = lookupServiceForBar(bar, serviceRows);
  const workflowGroup = String(bar.workflowGroup ?? row?.workflowGroup ?? '').trim();
  const workflowName = String(bar.workflowName ?? row?.workflowName ?? '').trim();

  return {
    label: bar.label || row?.name || workflowName,
    workflowGroup,
    workflowName,
    account: bar.account || row?.account,
  };
}

export function hasSingularWorkflowContext(target) {
  return (
    Boolean(String(target?.workflowGroup ?? '').trim()) &&
    Boolean(String(target?.workflowName ?? '').trim())
  );
}

/** Build workbench metrics query params from UI state. */
export function buildMetricsQueryParams({ baseAccount, filters, timeRange, quantity, rowcntMultiplier = 50 }) {
  const isAllMode = baseAccount?.toLowerCase() === 'all';
  const params = {};
  const qty = Math.min(99, Math.max(10, Number(filters.quantity) || 10));

  if (isAllMode) {
    params.accounts = '*';
  } else {
    params.account = baseAccount;
  }

  if (!isAllSelected(filters.workflowGroups)) {
    params.wfg_names = filters.workflowGroups.join(',');
  }

  const apiSpecific = !isAllSelected(filters.apis);
  if (apiSpecific) {
    const wfNames = [];
    const wfgNames = [];
    filters.apis.forEach((id) => {
      const { workflowGroup, workflowName } = parseApiSelection(id);
      if (workflowGroup) wfgNames.push(workflowGroup);
      if (workflowName) wfNames.push(workflowName);
    });
    if (wfgNames.length) params.wfg_names = [...new Set(wfgNames)].join(',');
    if (wfNames.length) params.wf_names = [...new Set(wfNames)].join(',');
  } else if (!isAllSelected(filters.workflows)) {
    params.wf_names = filters.workflows.join(',');
  }

  if (!apiSpecific && !isAllSelected(filters.adapters)) {
    params.adapter_types = filters.adapters.join(',');
  }

  if (timeRange?.from) params.start_date = formatDateParam(timeRange.from);
  if (timeRange?.to) params.end_date = formatDateParam(timeRange.to);

  params.rowcnt = Math.min(10000, qty * rowcntMultiplier);
  params.limit = qty;
  return params;
}

export function filtersAreDefault(filters, timeframe, customRange) {
  const def = DEFAULT_PERF_FILTERS;
  const today = defaultCustomRange();
  return (
    timeframe === TIMEFRAME_1D &&
    JSON.stringify(filters) === JSON.stringify(def) &&
    formatDateTimeDisplay(customRange.from) === formatDateTimeDisplay(today.from) &&
    formatDateTimeDisplay(customRange.to) === formatDateTimeDisplay(today.to)
  );
}

export function transformBySourceToChart(bySourceRows, quantity, serviceNameMap, serviceAccountMap) {
  const apiMap = new Map();
  for (const row of bySourceRows || []) {
    const key = `${row.wf_group}::${row.wf_name}`;
    if (!apiMap.has(key)) {
      apiMap.set(key, {
        id: key,
        label: serviceNameMap?.get(key) || row.wf_name || key,
        workflowGroup: String(row.wf_group ?? ''),
        workflowName: String(row.wf_name ?? ''),
        account: row.account || serviceAccountMap?.get(key),
        sources: new Map(),
        total: 0,
      });
    }
    const api = apiMap.get(key);
    const src = row.source_key || 'Unknown';
    api.sources.set(src, (api.sources.get(src) || 0) + (Number(row.trans_count) || 0));
    api.total += Number(row.trans_count) || 0;
  }

  const bars = [...apiMap.values()]
    .sort((a, b) => b.total - a.total)
    .slice(0, quantity);

  const sourceSet = new Set();
  bars.forEach((b) => b.sources.forEach((_, k) => sourceSet.add(k)));
  const series = [...sourceSet].map((key, i) => ({
    key,
    label: key,
    color: SOURCE_COLORS[i % SOURCE_COLORS.length],
  }));

  return {
    series,
    bars: bars.map((b) => ({
      id: b.id,
      label: b.label,
      total: b.total,
      workflowGroup: b.workflowGroup,
      workflowName: b.workflowName,
      account: b.account,
      segments: Object.fromEntries(b.sources),
    })),
  };
}

/** Aggregate by_source rows into outlier rows (same shape as conformance_outliers API). */
export function aggregateBySourceToOutliers(bySourceRows) {
  const apiMap = new Map();
  for (const row of bySourceRows || []) {
    const key = `${row.wf_group}::${row.wf_name}`;
    if (!apiMap.has(key)) {
      apiMap.set(key, {
        wf_group: row.wf_group,
        wf_name: row.wf_name,
        account: row.account,
        trans_count: 0,
        exec_time_viol_cnt: 0,
        err_validation_cnt: 0,
        err_translation_cnt: 0,
        err_generic_cnt: 0,
        success_non_conform_cnt: 0,
      });
    }
    const agg = apiMap.get(key);
    agg.trans_count += Number(row.trans_count) || 0;
    agg.exec_time_viol_cnt += Number(row.exec_time_viol_cnt) || 0;
    agg.err_validation_cnt += Number(row.err_validation_cnt) || 0;
    agg.err_translation_cnt += Number(row.err_translation_cnt) || 0;
    agg.err_generic_cnt += Number(row.err_generic_cnt) || 0;
    agg.success_non_conform_cnt += Number(row.success_non_conform_cnt) || 0;
  }
  return [...apiMap.values()].map((row) => ({
    ...row,
    violation_total:
      row.exec_time_viol_cnt +
      row.err_validation_cnt +
      row.err_translation_cnt +
      row.err_generic_cnt +
      row.success_non_conform_cnt,
  }));
}

export function transformOutliersToChart(outliers, quantity, serviceNameMap, labels = {}, serviceAccountMap) {
  const bars = (outliers || [])
    .map((row) => {
      const id = `${row.wf_group}::${row.wf_name}`;
      const segments = violationSegmentsFromRow(row);
      return {
        id,
        label: serviceNameMap?.get(id) || row.wf_name || id,
        workflowGroup: String(row.wf_group ?? ''),
        workflowName: String(row.wf_name ?? ''),
        account: row.account || serviceAccountMap?.get(id),
        transCount: Number(row.trans_count) || 0,
        total: violationSegmentTotal(segments),
        segments,
      };
    })
    .filter((b) => b.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, quantity);

  return {
    series: buildViolationStackSeries(labels),
    bars,
  };
}

/** Distinct integer Y-axis tick values for stacked charts (avoids duplicate labels when max is small). */
export function buildChartYAxisTicks(maxTotal) {
  const max = Math.max(1, Math.round(Number(maxTotal) || 0));
  if (max <= 5) {
    return Array.from({ length: max + 1 }, (_, i) => i);
  }
  const step = max <= 20 ? Math.max(1, Math.ceil(max / 5)) : Math.ceil(max / 5 / 10) * 10;
  const ticks = [0];
  for (let v = step; v < max; v += step) {
    ticks.push(v);
  }
  if (ticks[ticks.length - 1] !== max) {
    ticks.push(max);
  }
  return ticks;
}

/** Truncate long X-axis bar labels for rotated SVG text. */
export function truncateChartLabel(label, maxLen = 12) {
  const text = String(label ?? '');
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen - 1)}…`;
}

export function buildFilterOptionsFromServices(rows, selectedWfGroups = ['ALL']) {
  const workflowGroups = new Set();
  const workflows = new Set();
  const apis = [];
  const adapters = new Set();

  const wfgFilter = isAllSelected(selectedWfGroups)
    ? null
    : new Set(selectedWfGroups);

  for (const row of rows || []) {
    if (row.workflowGroup) workflowGroups.add(row.workflowGroup);
    if (wfgFilter && !wfgFilter.has(row.workflowGroup)) continue;
    if (row.workflowName) workflows.add(row.workflowName);
    if (row.adapter) adapters.add(row.adapter);
    apis.push({
      id: apiRowKey(row),
      label: row.name || row.workflowName,
      workflowGroup: row.workflowGroup,
      workflowName: row.workflowName,
    });
  }

  apis.sort((a, b) => a.label.localeCompare(b.label));

  return {
    workflowGroups: [...workflowGroups].sort((a, b) => a.localeCompare(b)),
    workflows: [...workflows].sort((a, b) => a.localeCompare(b)),
    apis,
    adapters: [...adapters].sort((a, b) => a.localeCompare(b)),
  };
}

/** Non-conforming transaction count from a metrics by_source row. */
export function getNonConformingCount(row) {
  return (
    (Number(row.exec_time_viol_cnt) || 0) +
    (Number(row.err_validation_cnt) || 0) +
    (Number(row.err_translation_cnt) || 0) +
    (Number(row.err_generic_cnt) || 0) +
    (Number(row.success_non_conform_cnt) || 0)
  );
}

export function clampQuantity(raw, fallback = DEFAULT_PERF_FILTERS.quantity) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(99, Math.max(10, Math.round(n)));
}

/** Singular view: top sources by transactions (conforming vs non-conforming stacks). */
export function transformSingularTransactionsBySource(bySourceRows, quantity, labels = {}) {
  const bars = (bySourceRows || [])
    .map((row) => {
      const trans = Number(row.trans_count) || 0;
      const nonConforming = Math.min(trans, getNonConformingCount(row));
      const conforming = Math.max(0, trans - nonConforming);
      const sourceKey = row.source_key || 'Unknown';
      return {
        id: sourceKey,
        label: sourceKey,
        sourceKey,
        workflowGroup: row.wf_group,
        workflowName: row.wf_name,
        total: trans,
        segments: { conforming, nonConforming },
      };
    })
    .filter((b) => b.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, quantity);

  return {
    series: [
      { key: 'conforming', label: labels.conforming || 'Conforming', color: '#6aaf6a' },
      { key: 'nonConforming', label: labels.nonConforming || 'Non-conforming', color: '#c45c5c' },
    ],
    bars,
  };
}

/** Singular view: top sources by conformance violations. */
export function transformSingularViolationsBySource(bySourceRows, quantity, labels = {}) {
  const sourceMap = new Map();

  for (const row of bySourceRows || []) {
    const sourceKey = row.source_key || 'Unknown';
    if (!sourceMap.has(sourceKey)) {
      sourceMap.set(sourceKey, {
        id: sourceKey,
        label: sourceKey,
        sourceKey,
        workflowGroup: row.wf_group,
        workflowName: row.wf_name,
        segments: { execTime: 0, validation: 0, translation: 0, errors: 0 },
      });
    }

    const entry = sourceMap.get(sourceKey);
    const segments = violationSegmentsFromRow(row);
    entry.segments.execTime += segments.execTime;
    entry.segments.validation += segments.validation;
    entry.segments.translation += segments.translation;
    entry.segments.errors += segments.errors;
  }

  const bars = [...sourceMap.values()]
    .map((bar) => ({
      ...bar,
      total: violationSegmentTotal(bar.segments),
    }))
    .filter((b) => b.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, quantity);

  return {
    series: buildViolationStackSeries(labels),
    bars,
  };
}

export function enrichSingularBySourceRows(rows, target) {
  if (!rows?.length || !target) return rows || [];
  const wg = String(target.workflowGroup ?? '').trim();
  const wn = String(target.workflowName ?? '').trim();
  const acc = target.account;
  return rows.map((row) => ({
    ...row,
    wf_group: row.wf_group || wg || row.wf_group,
    wf_name: row.wf_name || wn || row.wf_name,
    account: row.account || acc,
  }));
}

export function metricsSuccess(payload) {
  return payload?.current_state === 'SUCCESS' || payload?.status === 'SUCCESS';
}

/** True while a metrics query is in flight and no successful payload is available yet. */
export function isMetricsQueryPending(queryState, payload) {
  if (!queryState) return false;
  const pending = queryState.isLoading || queryState.isFetching;
  if (!pending) return false;
  return !metricsSuccess(payload);
}
